import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
    Avatar,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { ChatMessage } from "../../common/models";
import { copyToClipBoard } from "../../utils/copyContent";
import { useContext, useEffect, useState } from "react";
import { CheckroomSharp } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import UserIcon from "../../assets/FightLessWinMore_Cover-150px.png";
import { useAppStateContext } from "../../state/AppContextProvider";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AccessContext } from "../../state/AccessProvider";

interface Props {
    message: ChatMessage
    updateMessage: (message: ChatMessage) => void
}

const MuiUserMessage = ({ message, updateMessage }: Props) => {
    const { profilePhoto, exportChat, state } = useAppStateContext();
    const [copied, setCopied] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const accessContext = useContext(AccessContext);

    useEffect(() => {
        if (copied) {
            setTimeout(() => {
                setCopied(false);
            }, 3000);
        }
    }, [copied]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleExportConversation = async () => {
        setAnchorEl(null);
        try {
            const sessionId = state.currentConversation.sessionId;
            if (!sessionId) {
                const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
                DownloadNotificationService.error(
                    'Export Error',
                    'No active session found. Please start a conversation first.'
                );
                return;
            }
            const abortController = new AbortController();
            await exportChat("chathistory", sessionId, abortController.signal); // <-- use "chathistory"
        } catch (err) {
            const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
            DownloadNotificationService.error(
                'Export Failed',
                'There was an error exporting the conversation history. Please try again.'
            );
            console.error(err);
        }
    };

    const namePieces = accessContext.decodedToken?.name?.split(' ') ?? [];

    const avatarStyle = {
        width: 50,
        height: 50,
        objectFit: "contain" as const,
    }

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
            }}
        >
            <Stack
                direction={{ xs: "column-reverse", sm: "row" }} // avatar above message on mobile, right on desktop
                spacing={2}
                justifyContent="flex-end"
                width="100%"
                alignItems={{ xs: "flex-end", sm: "flex-start" }} // <-- align avatar to top of message
            >
                <Paper
                    sx={{
                        bgcolor: "secondary.natural_light_BG_glacial",
                        color: "primary.natural_light_BG_glacial",
                        border: "2px solid",
                        borderColor: "custom.brandPrimary",
                        p: 2,
                        pb: 1,
                        borderRadius: "12px 0px 12px 12px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        position: "relative",
                        minWidth: 0,
                        maxWidth: { xs: "100%", sm: "80%" },
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            width: "100%",
                            wordBreak: "break-word",
                            fontSize: { xs: "14px", sm: "15px" },
                            lineHeight: { xs: "1.2", sm: "1.5" },
                        }}
                    >
                        {message.content}
                    </Typography>
                    {/* Action icons at the bottom right */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                        <IconButton
                            size="small"
                            sx={{ padding: "1px", borderRadius: "30px", color: "custom.lockedPrimary" }}
                            onClick={() => {
                                setCopied(true);
                                copyToClipBoard(message.content)
                            }}
                            title={copied ? "Copied!" : "Copy"}

                        >
                            {copied
                                ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: 16 }} />
                                : <ContentCopyOutlinedIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                            size="small"
                            sx={{ padding: "1px", borderRadius: "30px", color: "custom.lockedPrimary" }}
                            onClick={() => updateMessage(message)}
                            title="Edit"
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            sx={{ padding: "1px", borderRadius: "30px", color: "custom.lockedPrimary" }}
                            onClick={handleMenuOpen}
                            title="More"
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            MenuListProps={{
                                onMouseLeave: handleMenuClose,
                                autoFocus: false,
                            }}
                            sx={{ color: "custom.lockedPrimary" }}
                        >
                            <MenuItem onClick={handleExportConversation}>
                                Export Conversation
                            </MenuItem>
                        </Menu>
                    </Box>
                </Paper>
                {!profilePhoto || /* not matches link */ !(/^https:.*/.test(profilePhoto ?? '')) ? (
                    /* Display user icon instead of initials */
                    <Box
                        component="img"
                        src={UserIcon}
                        alt="Profile"
                        sx={avatarStyle}
                    />
                ) : (
                    <Box
                        component="img"
                        src={profilePhoto || ""}
                        alt="Profile"
                        sx={avatarStyle}
                    />
                )}
            </Stack>
        </Box>
    )
};

export default MuiUserMessage;
